import { listings, orders, packets, pharmacies } from "../data/mockData";
import type { Bid, HoldRequest, Listing, Order, Pharmacy, TransactionPacket } from "../types/domain";

const API_BASE_URL = process.env.VITE_MATCHRX_API_BASE_URL || "https://xmwdpqt55i.execute-api.us-east-1.amazonaws.com";
const COGNITO_CLIENT_ID = process.env.VITE_MATCHRX_COGNITO_CLIENT_ID || "1a3au92qjr05a8fj4euk0ban0l";
const COGNITO_REGION = process.env.VITE_MATCHRX_COGNITO_REGION || "us-east-1";
const TOKEN_STORAGE_KEY = "matchrx.idToken";

export interface ApiListingPayload {
  listingId: string;
  ndc: string;
  productName: string;
  strength?: string;
  dosageForm?: string;
  lotNumber: string;
  unitPrice: number;
  quantityAvailable: number;
  expirationDate: string;
  sellerOrgId: string;
  sellerDisplayName?: string;
  sellerCity?: string;
  sellerState?: string;
  sellerRating?: number;
  packetStatus: "not_started" | "incomplete" | "pending_review" | "ready" | "rejected" | string;
  status: "draft" | "active" | "paused" | "held" | "sold_out" | "expired" | "removed" | "under_review" | string;
  tags?: string[];
  shipsInHours?: number;
}

export interface LoginResult {
  idToken: string;
  accessToken: string;
  expiresIn: number;
}

export interface ApiUserPayload {
  userId: string;
  email: string;
  status: string;
  emailVerified?: boolean;
}

export interface ApiMembershipPayload {
  membershipId: string;
  userId?: string;
  orgId: string;
  role: string;
  permissions?: string[];
  status: string;
}

export interface ApiOrganizationPayload {
  orgId: string;
  legalName: string;
  verificationStatus: string;
  primaryContactEmail?: string;
  city?: string;
  state?: string;
}

export interface ApiMePayload {
  user: ApiUserPayload | null;
  memberships: ApiMembershipPayload[];
  organizations: ApiOrganizationPayload[];
  requestId: string;
}

export interface CreateListingPayload {
  sellerOrgId: string;
  createdByUserId: string;
  productName: string;
  ndc: string;
  strength: string;
  dosageForm: string;
  packageSize: string;
  manufacturer: string;
  lotNumber: string;
  expirationDate: string;
  quantityAvailable: number;
  unitPrice: number;
  holdWindowMinutes: number;
  shippingOriginState: string;
}

function browserStorage(): Storage | undefined {
  return typeof window === "undefined" ? undefined : window.localStorage;
}

export function getStoredIdToken(): string | undefined {
  return browserStorage()?.getItem(TOKEN_STORAGE_KEY) || undefined;
}

export function setStoredIdToken(token: string): void {
  browserStorage()?.setItem(TOKEN_STORAGE_KEY, token);
}

export function clearStoredIdToken(): void {
  browserStorage()?.removeItem(TOKEN_STORAGE_KEY);
}

function normalizePacketStatus(status: ApiListingPayload["packetStatus"]): Listing["packetStatus"] {
  if (status === "ready") return "ready";
  if (status === "rejected") return "blocked";
  return "needs_review";
}

function normalizeListingStatus(status: ApiListingPayload["status"]): Listing["status"] {
  if (status === "held") return "held";
  if (status === "sold_out") return "sold";
  if (status === "active") return "active";
  return "needs_packet_review";
}

export function mapApiListing(apiListing: ApiListingPayload): Listing {
  const dosage = [apiListing.strength, apiListing.dosageForm].filter(Boolean).join(" ") || "Dose pending";
  return {
    id: apiListing.listingId,
    ndc: apiListing.ndc,
    name: apiListing.productName,
    dosage,
    lot: apiListing.lotNumber,
    unitPrice: Number(apiListing.unitPrice),
    quantity: Number(apiListing.quantityAvailable),
    expiresAt: apiListing.expirationDate,
    shipsInHours: Number(apiListing.shipsInHours ?? 24),
    sellerId: apiListing.sellerOrgId,
    packetStatus: normalizePacketStatus(apiListing.packetStatus),
    status: normalizeListingStatus(apiListing.status),
    tags: apiListing.tags ?? [],
  };
}

export function mapApiListingSeller(apiListing: ApiListingPayload): Pharmacy {
  return {
    id: apiListing.sellerOrgId,
    name: apiListing.sellerDisplayName || apiListing.sellerOrgId,
    city: apiListing.sellerCity || "Verified city",
    state: apiListing.sellerState || "US",
    licenseStatus: "verified",
    rating: Number(apiListing.sellerRating ?? 4.8),
  };
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getStoredIdToken();
  if (!token) {
    throw new Error("Sign in with a seeded MatchRx user before loading live marketplace data.");
  }
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(init.headers || {}),
    },
  });
  if (!response.ok) {
    const body = await response.text();
    throw new Error(`MatchRx API ${response.status}: ${body || response.statusText}`);
  }
  return response.json() as Promise<T>;
}

export const realApiClient = {
  apiBaseUrl: API_BASE_URL,
  async login(email: string, password: string): Promise<LoginResult> {
    const response = await fetch(`https://cognito-idp.${COGNITO_REGION}.amazonaws.com/`, {
      method: "POST",
      headers: {
        "content-type": "application/x-amz-json-1.1",
        "x-amz-target": "AWSCognitoIdentityProviderService.InitiateAuth",
      },
      body: JSON.stringify({
        AuthFlow: "USER_PASSWORD_AUTH",
        ClientId: COGNITO_CLIENT_ID,
        AuthParameters: { USERNAME: email, PASSWORD: password },
      }),
    });
    if (!response.ok) {
      const body = await response.text();
      throw new Error(`Cognito login failed ${response.status}: ${body || response.statusText}`);
    }
    const payload = await response.json();
    const auth = payload.AuthenticationResult;
    setStoredIdToken(auth.IdToken);
    return { idToken: auth.IdToken, accessToken: auth.AccessToken, expiresIn: auth.ExpiresIn };
  },
  logout: clearStoredIdToken,
  async me(): Promise<ApiMePayload> {
    return request<ApiMePayload>("/api/me");
  },
  async listListings(): Promise<ApiListingPayload[]> {
    const payload = await request<{ listings: ApiListingPayload[] }>("/api/listings");
    return payload.listings;
  },
  async getListing(id: string): Promise<ApiListingPayload | undefined> {
    const payload = await request<{ listing: ApiListingPayload }>(`/api/listings/${encodeURIComponent(id)}`);
    return payload.listing;
  },
  async createListing(input: CreateListingPayload): Promise<ApiListingPayload> {
    const payload = await request<{ listing: ApiListingPayload }>("/api/listings", {
      method: "POST",
      headers: { "x-request-id": `frontend-create-listing-${Date.now()}` },
      body: JSON.stringify(input),
    });
    return payload.listing;
  },
  async activateListing(listingId: string): Promise<{ listingId: string; status: string }> {
    return request<{ listingId: string; status: string }>(`/api/listings/${encodeURIComponent(listingId)}/activate`, {
      method: "POST",
      headers: { "x-request-id": `frontend-activate-listing-${Date.now()}` },
    });
  },
};

export interface MockSession {
  user: { id: string; email: string; role: "buyer" | "seller" | "admin" };
  organization: Pharmacy;
}

export interface AdminReviewItem {
  id: string;
  type: "listing" | "packet" | "user" | "dispute";
  label: string;
  status: "open" | "blocked" | "needs_review";
}

const mutableOrders: Order[] = [...orders];
const mutableBids: Bid[] = [];
const mutableHoldRequests: HoldRequest[] = [];

// Kept only for non-marketplace prototype surfaces that do not have backend endpoints yet.
export const mockApiClient = {
  listListings: (): Listing[] => listings,
  getListing: (id: string): Listing | undefined => listings.find(listing => listing.id === id),
  listOrders: (): Order[] => mutableOrders,
  getOrder: (id: string): Order | undefined => mutableOrders.find(order => order.id === id),
  createOrder: (order: Omit<Order, "id">): Order => {
    const created: Order = { ...order, id: `ORD-${1100 + mutableOrders.length}` };
    mutableOrders.push(created);
    return created;
  },
  listBids: (): Bid[] => mutableBids,
  createBid: (bid: Omit<Bid, "id">): Bid => {
    const created: Bid = { ...bid, id: `BID-${1700 + mutableBids.length}` };
    mutableBids.push(created);
    return created;
  },
  updateBidStatus: (id: string, status: Bid["status"]): Bid => {
    const bid = mutableBids.find(item => item.id === id);
    if (!bid) throw new Error(`Bid not found: ${id}`);
    bid.status = status;
    return bid;
  },
  listHoldRequests: (): HoldRequest[] => mutableHoldRequests,
  createHoldRequest: (holdRequest: Omit<HoldRequest, "id">): HoldRequest => {
    const created: HoldRequest = { ...holdRequest, id: `HOLD-${2400 + mutableHoldRequests.length}` };
    mutableHoldRequests.push(created);
    return created;
  },
  updateHoldRequestStatus: (id: string, status: HoldRequest["status"]): HoldRequest => {
    const holdRequest = mutableHoldRequests.find(item => item.id === id);
    if (!holdRequest) throw new Error(`Hold request not found: ${id}`);
    holdRequest.status = status;
    return holdRequest;
  },
  listPackets: (): TransactionPacket[] => packets,
  getPacket: (id: string): TransactionPacket | undefined => packets.find(packet => packet.id === id || packet.orderId === id),
  listPharmacies: (): Pharmacy[] => pharmacies,
  getPharmacy: (id: string): Pharmacy | undefined => pharmacies.find(pharmacy => pharmacy.id === id),
  getSession: (): MockSession => ({
    user: { id: "user-green-valley-owner", email: "owner@greenvalleyrx.example", role: "buyer" },
    organization: pharmacies[0],
  }),
  listReviewQueue: (): AdminReviewItem[] => [
    { id: "lst-trulicity", type: "listing", label: "Trulicity packet needs review", status: "needs_review" },
    { id: "pkt-1038", type: "packet", label: "ORD-1038 packet missing shipping SOP", status: "needs_review" },
  ],
};
